import React, { useState, useCallback, useMemo } from 'react';
import { isEmpty, uniqBy } from 'lodash-es';
import PropTypes from 'prop-types';
import { Skeleton, Button, Comment, Avatar } from 'antd';
import i18next from 'i18next';
import { getRepliesOfRating } from 'api/ratings';
import { useSelector } from 'react-redux';
import { UserOutlined } from '@ant-design/icons';

import { getFeaturedImage } from 'utils/dataUtils';
import CommentForm from './CommentForm';
import ReplyItem from './ReplyItem';
import { ReplySectionStyles } from './styles';

const limitDefault = 10;

const RepliesSection = ({
  id,
  latestReplyReview,
  totalReplies,
  visibleReply,
}) => {
  const { images, name } =
    useSelector((state) => state.centres?.currentData) || {};

  const featureImage = getFeaturedImage(images);

  const [replies, setReplies] = useState({
    offset: 0,
    total: Number(totalReplies) || 0,
    data: latestReplyReview ? [latestReplyReview] : [],
  });

  const [loading, setLoading] = useState(false);

  const enabledLoadMore = useMemo(() => {
    if (Number(totalReplies) < 2) return false;
    return !loading && replies?.data?.length < replies.total;
  }, [loading, replies, totalReplies]);

  const handleViewMore = useCallback(() => {
    setLoading(true);

    id &&
      getRepliesOfRating(id, {
        limit: limitDefault,
        orderBy: '-createdAt',
        offset: replies.offset,
      })
        .then((response) => {
          setReplies({
            offset: replies.offset + limitDefault,
            total: response.total || 0,
            data: uniqBy([...replies.data, ...(response.results || [])], 'id'),
          });
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
  }, [id, replies]);

  const handleChangeReplies = (record) => {
    setReplies({
      offset: replies.offset,
      total: replies.total || 0 + 1,
      data: [record, ...replies.data],
    });
  };

  return (
    <ReplySectionStyles>
      <div>
        {visibleReply && (
          <Comment
            avatar={
              <Avatar
                size={40}
                src={featureImage}
                alt=""
                icon={<UserOutlined />}
              />
            }
            content={
              <CommentForm id={id} handleChangeReplies={handleChangeReplies} />
            }
          />
        )}
        {!loading && isEmpty(replies?.data) ? null : (
          <div>
            {replies?.data.map((item, index) => (
              <ReplyItem
                item={item}
                key={String(index)}
                logoUrl={featureImage}
                centreName={name}
              />
            ))}
          </div>
        )}
        {loading && <Skeleton active avatar />}
        {enabledLoadMore && (
          <Button type="link" onClick={handleViewMore}>
            {i18next.t('button.viewMoreComments')}
          </Button>
        )}
      </div>
    </ReplySectionStyles>
  );
};

RepliesSection.propTypes = {
  latestReplyReview: PropTypes.object,
  id: PropTypes.string,
  totalReplies: PropTypes.any,
  visibleReply: PropTypes.bool,
};

export default RepliesSection;
